<template>
  <div class="page-main flex-page">
    <!--Header-->
    <PageHeader
      :content="headerContent"
      :disableButton="showOptions.disableButton || showOptions.saveInProgress"
      :showHeaderShadow="showOptions.headerShadow"
      @buttonClicked="updateApp"
    ></PageHeader>

    <!--Main Content-->
    <div
      v-if="!lodash.isEmpty(this.selectedApp)"
      ref="mainContent"
      class="main-content"
      @scroll="contentScroll"
    >
      <SettingsForm
        v-for="field in fields"
        :key="field._id"
        :fields="field.fields"
        :content="field.content"
        @fieldChanged="fieldChanged"
      ></SettingsForm>
    </div>

    <!--Pre Loader-->
    <PreLoader v-else :show="true" class="main-content"></PreLoader>

    <!--Notification Message-->
    <NotificationMessage
      :show="status.show"
      :status="status.status"
      :title="status.title"
    ></NotificationMessage>

    <!--Loader-->
    <LineLoader :show="showOptions.lineLoader"></LineLoader>

    <!--Unsaved Changes Modal -->
    <UnsavedChangesModal
      :show="showOptions.unsavedChanges"
      @primaryEvent="continueRoute"
      @secondaryEvent="closeModal"
    ></UnsavedChangesModal>
  </div>
</template>
                
<script>
// Libraries
import _ from "lodash";

// Components
import PageHeader from "@/components/dashboard/PageHeader";
import NotificationMessage from "@/components/modals/NotificationMessage";
import PreLoader from "@/components/loaders/PreLoader";
import LineLoader from "@/components/loaders/LineLoader";
import SettingsForm from "@/components/form/SettingsForm";
import UnsavedChangesModal from "@/components/modals/UnsavedChangesModal";

// Defaults
import { LANGUAGES, APP_NAMES } from "@/mixins/Variables";

// Services
import { TranslationsAppService } from "@/services";
const { CompanyService } = TranslationsAppService;

export default {
  name: "Settings",
  data() {
    return {
      lodash: _,
      showOptions: {
        headerShadow: false,
        lineLoader: false,
        disableButton: true,
        unsavedChanges: false,
      },
      headerContent: {
        title: "Settings",
        description: "Manage languages and translation rules for your tickets",
      },
      fields: {
        settings: {
          content: {},
          fields: {
            defaultLanguage: {
              type: "dropdown",
              placeholder: "Select language",
              title: "Default language for translations",
              required: true,
              hasError: false,
              isSingleRow: true,
              fields: LANGUAGES,
              value: "",
            },
            knownLanguages: {
              type: "multiple-select-dropdown",
              placeholder: "Select languages",
              title: "Ignore translations for specific languages",
              required: false,
              hasError: false,
              isSingleRow: true,
              fields: LANGUAGES,
              value: [],
            },
            promptInstructions: {
              type: "textarea",
              placeholder: "Add an instruction for the AI",
              required: false,
              hasError: false,
              title: "Instructions for the AI while translating a message",
              value: "",
            },
            internalNotes: {
              type: "group",
              nestFields: true,
              title: "Automatic internal notes",
              fields: {
                customerMessage: {
                  type: "toggle",
                  title: "Automatically add note for customer messages",
                  description: "Add the translation as an internal note",
                  isSingleRow: true,
                  value: false,
                },
                agentMessage: {
                  type: "toggle",
                  title: "Automatically add note for agent responses",
                  description: "Add the translation as an internal note",
                  isSingleRow: true,
                  value: false,
                },
              },
            },
            tags: {
              type: "group",
              nestFields: true,
              title: "Ticket tags",
              fields: {
                ticketTranslated: {
                  type: "text",
                  placeholder: "Add a tag",
                  required: false,
                  hasError: false,
                  title: "Tag for ticket translations",
                  description:
                    "This will be inserted when any ticket is translated",
                  isSingleRow: true,
                  value: "",
                },
                responseTranslated: {
                  type: "text",
                  placeholder: "Add a tag",
                  required: false,
                  hasError: false,
                  title: "Tag for response translations",
                  description:
                    "This is inserted when an agent translates their response",
                  isSingleRow: true,
                  value: "",
                },
              },
            },
          },
        },
      },
      initData: {},
      dataChanged: false,
      status: {
        show: false,
        status: "success",
        title: "Settings saved",
      },
    };
  },
  props: {},
  watch: {},
  components: {
    PageHeader,
    NotificationMessage,
    PreLoader,
    LineLoader,
    SettingsForm,
    UnsavedChangesModal,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
  },
  async created() {
    await this.initApp();
  },
  methods: {
    // Initialise the app
    async initApp() {
      if (_.isEmpty(this.selectedApp)) {
        const data = { companyId: this.company._id };
        const response = await CompanyService.GetCompany(data);

        this.$store.commit("updateSelectedApp", {
          appName: APP_NAMES.translations,
          ...response.data,
        });
      }

      this.populateFields();
    },

    // Update app
    async updateApp() {
      this.showOptions.lineLoader = true;

      const data = this.fields.settings.fields;
      var isFormValid = this.validateForm(data);
      if (isFormValid) {
        const payload = this.constructPayload();
        const response = await CompanyService.UpdateSettings(payload);

        if (response.hasError) {
          this.status.status = "error";
          this.status.message = response.message;
        } else if (!response.hasError) {
          this.status.status = "success";
          this.status.message = response.message;

          this.$store.commit("updateSelectedApp", {
            appName: APP_NAMES.translations,
            ...response.data,
          });
          this.populateFields();
        }

        this.showStatusMessage(this.status);
      }

      this.showOptions.lineLoader = false;
    },

    // Populate the fields
    populateFields() {
      if (!_.isEmpty(this.selectedApp)) {
        const { settings } = this.selectedApp;
        const {
          settings: { fields },
        } = this.fields;

        fields.internalNotes.fields.customerMessage.value =
          settings.internalNotes.customerMessage;
        fields.internalNotes.fields.agentMessage.value =
          settings.internalNotes.agentMessage;

        fields.promptInstructions.value = settings.promptInstructions;

        fields.defaultLanguage.value = settings.defaultLanguage;
        fields.knownLanguages.value = settings.knownLanguages;

        fields.tags.fields.ticketTranslated.value =
          settings.tags.ticketTranslated;
        fields.tags.fields.responseTranslated.value =
          settings.tags.responseTranslated;
      }

      this.initFormData();
    },

    // Construct the payload
    constructPayload() {
      const payload = {
        companyId: this.company._id,
        settings: this.parseData(this.fields.settings.fields),
      };

      return payload;
    },

    // Initialise the form data for comparisons
    initFormData() {
      const data = this.fields;

      this.disableSaveButton(true);
      this.initData = _.cloneDeep(data);
    },

    // Show the modal header options
    disableSaveButton(status) {
      this.dataChanged = !status;
      this.showOptions.disableButton = status;
    },

    // Update rule when fields change
    fieldChanged() {
      const data = this.fields;
      this.disableSaveButton(this.isDataEqual(this.initData, data));
    },

    // Event on scroll
    contentScroll() {
      const scrollTop = this.$refs.mainContent.scrollTop;

      // Check if header needs shadow
      if (scrollTop > 0) this.showOptions.headerShadow = true;
      else this.showOptions.headerShadow = false;
    },

    // Close all the modals
    closeModal() {
      this.showOptions.unsavedChanges = false;
    },

    // Discard all the changes
    discardChanges() {
      this.fields = _.cloneDeep(this.initData.fields);
      this.disableSaveButton(true);
      this.closeModal();
    },

    // Discard the changes and move to the selected route
    continueRoute() {
      this.closeModal();
      this.discardChanges();
      if (this.toRoute != null) this.$router.push({ path: this.toRoute.path });
    },
  },

  // Check if there are any changes to be saved
  beforeRouteLeave(to, from, next) {
    if (this.dataChanged) {
      this.toRoute = to;
      this.showOptions.unsavedChanges = true;
    } else {
      next();
    }
  },
};
</script>
                
<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";
</style>